import React, { ReactNode, useEffect, useState } from "react";
import Modal from "./index";
import Button from "../../Button/Button";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import * as api from "../../api";
import { nanoid } from "nanoid";
import { Helper } from "../../utils/Helper/HelperUtil";
import {
	userChosenPropertyId,
	userChosenPropertyType,
	userChosenPropertyAddress,
	userChosenRepairLocationId,
	userChosenRepairLocation,
	userChosenHighLevelDescription,
	userChosenSupplementaryQuestions,
	userChosenEmergencyQuestion,
	userChosenDuplicateQuestion,
	userChosenAdditionalDescription,
	userChosenVandalismQuestion,
	userChosenVandalismAdditionalInformation,
	userChosenCovidQuestion,
	userChosenVulnerabilityQuestion,
	userChosenVulnerabilityAdditionalInformation,
	userChosenContactDetails,
	userChosenUPRN,
	userChosenSOR,
	userChosenSTATUS,
	userChosenEligibilityErrorCode,
	userChosenContactPhone,
	userChosenContactName,
	userChosenRelationDetails,
	dmcCheckSavedAnswer
} from "../../ducks/redux/actions/userChoices";

import {
	selectedRepairReasonPriorityOverride,
	selectedRepairReasonDefaultPriority,
	sorErrorCode
} from "../../ducks/redux/actions/dashboardReducer.actions";
import {
	getHeatingHotWaterCase,
	getMaintResp,
	getRepairResp,
	getContractorName,
	getDeterminedContractorDetails,
	getContractorPayload,
} from "../../ducks/redux/actions/getAddress";
import { logError } from "../../utils/Helper/logError";

interface ModalProps {
	id?: string;
	dataTestId?: string;
	exit?: () => void;
	saveDraftAndExit?: (e?: any) => void;
	showSaveAndExit?: boolean; // new
	closeAfterSaving?: boolean;
	canSaveAndExit?: boolean;
	disabled?: boolean;
	open?: boolean;
	modalTitle?: string | ReactNode;
	modalSubtitle?: string | ReactNode;
	stayButtonText?: string;
	leaveButtonText?: string;
	showBackOnly?: boolean;
	onLeave?: () => void;
	onClose?: () => void;
}

const ExitModalAndSave: React.FC<ModalProps> = ({
	exit = () => { },
	saveDraftAndExit = () => { },
	closeAfterSaving = false,
	id = `exit-modal-${nanoid()}`,
	dataTestId = "exit-modal",
	canSaveAndExit,
	disabled = false,
	open,
	modalTitle,
	modalSubtitle,
	stayButtonText,
	leaveButtonText,
	showBackOnly = false,
	showSaveAndExit = true,
	onLeave,
	onClose
}) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [isDisable, setIsDisable] = useState(false);

	const workOrderObject = useSelector(
		(state: any) => state.addresses?.workOrderRepair
	);

	const isSurvey = useSelector(
		(state: any) => state.userChoicesReducer?.isSurvey
	);

	const isEDRepair = workOrderObject?.isEnhancedDiagnosticsRepair;

	const userChosenSORValue = useSelector(
		(state: any) => state?.userChoicesReducer?.userChosenSOR
	);
	

	const isSaveAndExitAllowed = useSelector((state: any) => state?.userChoicesReducer?.isSaveAndExitAllowed);
	// Hiding Save and Exit for ED Phase 1 users' repairs
	const finalCanSaveAndExit = typeof canSaveAndExit === "boolean" ? canSaveAndExit : (!isEDRepair && !isSurvey && userChosenSORValue !== "");

	useEffect(() => {
		if (closeAfterSaving) {
			setIsOpen(false);
		}
	}, [closeAfterSaving]);

	useEffect(() => {
		const urlPathName = window.location.pathname.slice(1, window.location.pathname.length);
		if (isSaveAndExitAllowed && (urlPathName === "RepairLocation" || urlPathName === "repairlocation" || urlPathName === "ConfirmationDetails" || urlPathName === "vulnerabilityquestion")) {
			setIsDisable(true)
		}
		else {
			setIsDisable(false)
		}
	}, [isSaveAndExitAllowed]);

	const triggerCancelButton = () => {
		const cancelButton = document.querySelector('img.cursor-pointer[alt="Cancel"]');
		if (cancelButton) {
			(cancelButton as HTMLElement).click();
		}
	};

	const clearUserStates = async () => {
		dispatch(dmcCheckSavedAnswer(""))
		dispatch(sorErrorCode(""));
		dispatch(userChosenPropertyId(""));
		dispatch(userChosenPropertyType(""));
		dispatch(userChosenPropertyAddress(""));
		dispatch(userChosenRepairLocationId(0));
		dispatch(userChosenRepairLocation(""));
		dispatch(userChosenHighLevelDescription(""));
		dispatch(userChosenSupplementaryQuestions(""));
		dispatch(userChosenEmergencyQuestion(""));
		dispatch(userChosenDuplicateQuestion(""));
		dispatch(userChosenAdditionalDescription(""));
		dispatch(userChosenVandalismQuestion("No"));
		dispatch(userChosenVandalismAdditionalInformation(""));
		dispatch(userChosenCovidQuestion(""));
		dispatch(userChosenVulnerabilityQuestion(""));
		dispatch(userChosenVulnerabilityAdditionalInformation(""));
		dispatch(userChosenContactDetails(""));
		dispatch(userChosenUPRN(""));
		dispatch(userChosenSOR(""));
		dispatch(getRepairResp(""));
		dispatch(getHeatingHotWaterCase(""));
		dispatch(getMaintResp(""));
		dispatch(getDeterminedContractorDetails(""));
		dispatch(getContractorName(""));
		dispatch(getContractorPayload(""));
		dispatch(userChosenSTATUS(""));
		dispatch(userChosenEligibilityErrorCode(""));
		dispatch(userChosenContactPhone(""));
		dispatch(userChosenContactName(""));
		dispatch(userChosenRelationDetails(""));
		dispatch(selectedRepairReasonPriorityOverride(null));
		dispatch(selectedRepairReasonDefaultPriority(null));
		exit();
		setIsOpen(false);
		if (isEDRepair) {
			await triggerEDAbandonedStatus();
		}

		if (workOrderObject) {

			const getWorkOrderResult: any = await api.getRepair(workOrderObject.id);
			await api.deleteWorkOrder(workOrderObject.id, getWorkOrderResult?.eTag?.toString());
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
			setIsOpen(true);
		}
	};

	useEffect(() => {
		if (typeof open === "boolean") setIsOpen(open);
	}, [open]);

	const handleStay = () => {
		setIsOpen(false);
		if (onClose) onClose();
	};

	const handleBack = async () => {

		if (isEDRepair &&
			(window.location.href.includes("RepairEnhancedDiagnostic") ||
				window.location.href.includes("ConfirmationDetails")
			)
		) {
			await triggerEDAbandonedStatus();
		}

		if (onLeave) {
			setIsOpen(false);
			if (onClose) onClose();
			onLeave();
			return;
		}
		await clearUserStates();
	};


	const triggerEDAbandonedStatus = async () => {
		try {
			await api.updateRepairDiagnosticsSession(workOrderObject?.repairDiagnosticsSessionIdentifier, {
				status: Helper.RepairEnhancedDiagnosticStatus.Abandoned
			});
		} catch (err) {
			logError(`Error updating diagnostics session to ${Helper.RepairEnhancedDiagnosticStatus.Abandoned}`, err);
		}
	}

	return (
		<>
			{!showBackOnly && (
				<div id={id} data-testid={dataTestId}>
					<button
						type="button"
						id="exitmodal-btn1"
						className="back-btn text-teal exit-btn a-style cursor-pointer"
						onClick={() => { if (!disabled) setIsOpen(true); }}
						onKeyDown={handleKeyDown}
						tabIndex={disabled ? -1 : 0}
						disabled={disabled}
					>
						{t("exit_button")}
					</button>
				</div>
			)}
			<Modal open={isOpen} cssClasses="modalPopUp2">
				<div id="exitmodal-div1" className="modalPopUpContainer container my-1">
					<div id="exitmodal-div2" className="py-lg-0 py-sm-0 h5 my-5 question-font-size text-center global-txt">
						{modalTitle || t("SSR030ai")}
						<br></br><br></br>
						{modalSubtitle || t("SSR030aii")}
					</div>
					<div id="exitmodal-div3" className='modalButtonContainer button-positions flex flex-wrap text-center pad-bot pt-lg-0'>
						{showBackOnly ? (
							<>
								<Button disabled={disabled} id="exitmodal-btn3" onClick={handleBack} className="button-class">{leaveButtonText || "Yes, go back"}
									<i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
								</Button>
								<Button disabled={disabled} id="exitmodal-btn2" className="button-class" onClick={handleStay}>{stayButtonText || "No, stay here"}
									<i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
								</Button>
							</>
						) : (
							<>
								<Button disabled={disabled} id="exitmodal-btn2" className="button-class mx-2" onClick={handleStay}>{t("return_repair_button")}
									<i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i></Button>
								<Button disabled={disabled} id="exitmodal-btn3" onClick={clearUserStates} className="button-class">{t("leave_button")}
									<i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i></Button>
								{finalCanSaveAndExit && showSaveAndExit && (
									<Button disabled={disabled || isDisable} id="exitmodal-btn4" onClick={() => {
										saveDraftAndExit?.();
										setTimeout(() => triggerCancelButton(), 0);
									}} className="button-class mx-2">{t("save_and_exit_button")}
										<i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i></Button>
								)}
							</>
						)}
					</div>
				</div>
			</Modal>
		</>
	);
};
export default ExitModalAndSave;
